import React from 'react';
import type { ViolationData, ViolationFixability } from '../../types/violation';
import { isUnfixableViolation } from '../../utils/violationFixability';
import FixabilityEvidenceBlock from './FixabilityEvidenceBlock';

interface ViolationFixabilityControlsProps {
    violation: ViolationData;
    /** Guideline 修正不可内容 for this page domain (from GET violations API). */
    mediaRuleDescription?: string;
    onFixabilityChange?: (fixability: ViolationFixability) => void;
    /** Edit mode: update which excerpt of the media rule explains unfixability (via text selection). */
    onUnfixableReasonPartChange?: (part: string) => void;
    /** View-only (zoom panel): status + 根拠 only, no toggle buttons. */
    readOnly?: boolean;
    compact?: boolean;
}

const ViolationFixabilityControls: React.FC<ViolationFixabilityControlsProps> = ({
    violation,
    mediaRuleDescription,
    onFixabilityChange,
    onUnfixableReasonPartChange,
    readOnly = false,
    compact = false,
}) => {
    const unfixable = isUnfixableViolation(violation);
    const showActions = !readOnly && Boolean(onFixabilityChange);
    const canEditPart = showActions && unfixable && Boolean(onUnfixableReasonPartChange);

    if (readOnly && !unfixable) {
        return null;
    }

    return (
        <div
            className="violation-fixability-controls"
            style={{
                marginTop: compact ? '8px' : showActions ? '16px' : 0,
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #e9d5ff',
                backgroundColor: '#ffffff',
            }}
        >
            {!readOnly && (
                <div style={{ marginBottom: showActions ? '8px' : 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                        修正可否
                    </span>
                </div>
            )}

            {showActions && (
                <div style={{ display: 'flex', gap: '6px', marginBottom: unfixable ? '8px' : 0 }}>
                    <button
                        type="button"
                        onClick={() => onFixabilityChange!('fixable')}
                        style={actionButtonStyle(violation.fixability === 'fixable', false)}
                    >
                        修正可能にする
                    </button>
                    <button
                        type="button"
                        onClick={() => onFixabilityChange!('unfixable')}
                        style={actionButtonStyle(unfixable, true)}
                    >
                        修正不可にする
                    </button>
                </div>
            )}

            {unfixable && (
                <div
                    style={{
                        paddingTop: showActions ? '8px' : 0,
                        borderTop: showActions ? '1px solid #e9d5ff' : 'none',
                    }}
                >
                    {readOnly && (
                        <span
                            style={{
                                display: 'inline-block',
                                fontSize: '11px',
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: '10px',
                                backgroundColor: '#ede9fe',
                                color: '#5b21b6',
                                border: '1px solid #c4b5fd',
                                marginBottom: '8px',
                            }}
                        >
                            修正不可
                        </span>
                    )}
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#5b21b6', marginBottom: '6px' }}>
                        根拠（メディア会社説明）
                        {canEditPart && (
                            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
                                — 該当部分をドラッグして選択
                            </span>
                        )}
                    </div>

                    <div
                        style={
                            readOnly
                                ? { maxHeight: '200px', overflowY: 'auto', paddingRight: '4px' }
                                : { maxHeight: '160px', overflowY: 'auto', paddingRight: '4px' }
                        }
                    >
                        <FixabilityEvidenceBlock
                            mediaRuleDescription={mediaRuleDescription}
                            highlightPart={violation.unfixableReasonDescriptionPart}
                            selectable={canEditPart}
                            onSelectPart={canEditPart ? onUnfixableReasonPartChange : undefined}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

function actionButtonStyle(active: boolean, unfixableVariant: boolean): React.CSSProperties {
    const activeColors = unfixableVariant
        ? { bg: '#7c3aed', border: '#6d28d9', color: '#fff' }
        : { bg: '#3b82f6', border: '#2563eb', color: '#fff' };
    const inactive = { bg: '#ffffff', border: '#d1d5db', color: '#374151' };
    const c = active ? activeColors : inactive;
    return {
        flex: 1,
        padding: '6px 8px',
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: '6px',
        border: `1px solid ${c.border}`,
        backgroundColor: c.bg,
        color: c.color,
        cursor: 'pointer',
    };
}

export default ViolationFixabilityControls;
